// ts-rest contract of the EDI corporate-actions feed (SEP-360 section 1,
// SEP-374).
//
//   GET /api/coac/edi/overview      the EDI register, the reconciliation with
//                                   Titan and the EDI entries of the calendar
//   GET /api/coac/edi/events/:id    one EDI event with its options and notes
//
// Outside ts-rest (multipart): POST /api/coac/edi-feed/uploads takes one or
// more files of the feed (`files`, or one file as `file`): e2020_LST_EVT,
// e2020_NTS and e2020_LUP. The title line of each file gives its kind.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { errorBodyWithCode as errorBody } from '../schemas/common';

const c = initContract();

/**
 * The source state of an EDI event.
 *
 * - `none`: Titan reported the same corporate action, or the event is not live.
 * - `edi_only`: Titan did not report it yet. The calendar shows an asterisk.
 * - `titan_not_received`: Titan did not report it, and its relevant date is
 *   near. The calendar shows the stronger flag.
 */
export const ediWarningSchema = z.enum(['none', 'edi_only', 'titan_not_received']);
export type EdiWarning = z.infer<typeof ediWarningSchema>;

export const ediRegisterRowSchema = z.object({
    id: z.string(),
    eventCode: z.string(),
    eventType: z.string(),
    eventSubType: z.string().nullable(),
    participation: z.enum(['mandatory', 'voluntary', 'both']).nullable(),
    status: z.enum(['active', 'cancelled', 'deleted']),
    /**
     * The name of the security: the internal name of the firm (the holdings,
     * by ISIN), else the EDI issuer name, else the EDI security description.
     */
    securityName: z.string().nullable(),
    /** The source of `securityName`, or null when no source gives a name. */
    securityNameSource: z.enum(['security_master', 'edi_issuer', 'edi_description']).nullable(),
    issuerName: z.string().nullable(),
    isin: z.string().nullable(),
    sedol: z.string().nullable(),
    listCountry: z.string().nullable(),
    exDate: z.string().nullable(),
    recordDate: z.string().nullable(),
    payDate: z.string().nullable(),
    effectiveDate: z.string().nullable(),
    /** The deadline of the event (`ediDeadlineOf` in the server), or null. */
    deadline: z.string().nullable(),
    /** The EDI column that gives `deadline`, for example `EndDT`, or null. */
    deadlineSource: z.string().nullable(),
    latestChangeAt: z.string(),
    firstSeenAt: z.string(),
    lastFileName: z.string(),
    /** The Titan corporate action that reports the same event, or null. */
    titanActionId: z.string().nullable(),
    titanSecurityName: z.string().nullable(),
    warning: ediWarningSchema,
    /** The date that drives the warning, or null. */
    relevantDate: z.string().nullable(),
});
export type EdiRegisterRow = z.infer<typeof ediRegisterRowSchema>;

/** One EDI date on the corporate-actions calendar. */
export const ediCalendarEntrySchema = z.object({
    key: z.string(),
    ediEventId: z.string(),
    /** The matched Titan corporate action, or null for an EDI-only event. */
    coacActionId: z.string().nullable(),
    kind: z.enum(['deadline', 'ex_date', 'record_date', 'pay_date']),
    date: z.string(),
    securityName: z.string(),
    eventType: z.string(),
    warning: ediWarningSchema,
});
export type EdiCalendarEntry = z.infer<typeof ediCalendarEntrySchema>;

export const ediOverviewSchema = z.object({
    events: z.array(ediRegisterRowSchema),
    /** The Titan corporate actions that EDI also reports, with the EDI event. */
    reconciliation: z.array(z.object({ coacActionId: z.string(), ediEventId: z.string() })),
    /**
     * The EDI dates of the calendar. A matched event gives only the dates that
     * differ from the Titan date of the same kind, so the calendar shows one
     * entry for one corporate action and keeps the dates of both providers.
     */
    calendar: z.array(ediCalendarEntrySchema),
    /** The text of the stronger flag. */
    notReceivedText: z.string(),
});
export type EdiOverview = z.infer<typeof ediOverviewSchema>;

export const ediEventDetailSchema = ediRegisterRowSchema.extend({
    notes: z.string().nullable(),
    options: z.array(
        z.object({
            optionId: z.string().nullable(),
            paytype: z.string().nullable(),
            defaultOption: z.boolean(),
            status: z.enum(['active', 'cancelled', 'deleted']),
            rateCurrency: z.string().nullable(),
            rates: z.array(z.object({ label: z.string(), value: z.string() })),
            ratioOld: z.string().nullable(),
            ratioNew: z.string().nullable(),
            outIsin: z.string().nullable(),
            optionElectionDate: z.string().nullable(),
        }),
    ),
    otherDates: z.array(z.object({ label: z.string(), date: z.string() })),
    discrepancies: z.array(z.string()),
    /** The dates of the matched Titan corporate action, for a side-by-side view. */
    titanDates: z
        .object({
            responseDeadline: z.string().nullable(),
            exDate: z.string().nullable(),
            recordDate: z.string().nullable(),
            payDate: z.string().nullable(),
        })
        .nullable(),
});
export type EdiEventDetail = z.infer<typeof ediEventDetailSchema>;

/** The kind of one file of the feed: the events, the notes or the lookups. */
export const ediFeedFileKindSchema = z.enum(['events', 'notes', 'lookups']);
export type EdiFeedFileKind = z.infer<typeof ediFeedFileKindSchema>;

/** The answer of an upload of the files of the feed. */
export const ediFeedUploadResultSchema = z.object({
    /** Each file of the upload, with the kind that its title line gives. */
    files: z.array(
        z.object({ fileName: z.string(), fileTitle: z.string(), kind: ediFeedFileKindSchema }),
    ),
    fileCount: z.number().int(),
    /**
     * True when a LUP file of the upload replaced the stored lookups. False
     * when the upload holds no LUP file, or when the stored LUP file is newer.
     */
    lookupsStored: z.boolean(),
    /** The stored events that got a name from the new lookups in place of the raw code. */
    eventTypesNamed: z.number().int(),
    rows: z.number().int(),
    events: z.number().int(),
    kept: z.number().int(),
    outOfScope: z.number().int(),
    created: z.number().int(),
    updated: z.number().int(),
    problems: z.array(z.string()),
});
export type EdiFeedUploadResult = z.infer<typeof ediFeedUploadResultSchema>;

export const coacEdiContract = c.router(
    {
        overview: {
            method: 'GET',
            path: '/coac/edi/overview',
            responses: { 200: ediOverviewSchema },
            summary: 'Ops: the EDI register, its reconciliation with Titan and its calendar dates',
        },
        event: {
            method: 'GET',
            path: '/coac/edi/events/:id',
            pathParams: z.object({ id: z.string() }),
            responses: { 200: ediEventDetailSchema, 404: errorBody },
            summary: 'Ops: one EDI event with its options, its notes and the matched Titan dates',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
